/**
 * 予約投稿向け簡易クロップ（Instagram風 cover + パン/ズーム）
 */

export const FEED_CROP_PRESETS = [
    { key: 'portrait', label: '4:5', ratio: 0.8 },
    { key: 'square', label: '1:1', ratio: 1.0 },
    { key: 'landscape', label: '横長', ratio: 1.91 },
];

export const STORY_CROP_PRESET = { label: '9:16', ratio: 9 / 16 };

export const FEED_RATIO_MIN = 0.8;
export const FEED_RATIO_MAX = 1.91;
export const STORY_RATIO_MIN = 0.5;
export const STORY_RATIO_MAX = 0.67;
export const RATIO_TOLERANCE = 0.02;

const OUTPUT_WIDTH = 1080;

export function isVideoFile(file) {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    if (type.startsWith('video/')) return true;
    return /\.(mp4|mov|m4v)$/i.test(file.name || '');
}

export function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
                ratio: img.naturalWidth / img.naturalHeight,
            });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('画像の読み込みに失敗しました。'));
        };
        img.src = url;
    });
}

export function isRatioValidForPostType(postType, ratio) {
    if (postType === 'feed') {
        return ratio >= FEED_RATIO_MIN && ratio <= FEED_RATIO_MAX;
    }
    if (postType === 'story') {
        return ratio >= STORY_RATIO_MIN && ratio <= STORY_RATIO_MAX;
    }

    return true;
}

export function getCropPresets(postType) {
    if (postType === 'story') {
        return [STORY_CROP_PRESET];
    }
    if (postType === 'feed') {
        return FEED_CROP_PRESETS;
    }

    return [];
}

export function pickDefaultCropRatio(postType, imageRatio, referenceRatio = null) {
    if (referenceRatio != null && referenceRatio > 0) {
        return referenceRatio;
    }

    const presets = getCropPresets(postType).map((p) => p.ratio);
    if (presets.length === 0) {
        return 1.0;
    }

    return presets.reduce((best, ratio) =>
        Math.abs(ratio - imageRatio) < Math.abs(best - imageRatio) ? ratio : best
    );
}

export function needsCrop(postType, imageRatio, referenceRatio = null) {
    if (postType === 'reel') {
        return false;
    }

    if (!isRatioValidForPostType(postType, imageRatio)) {
        return true;
    }

    if (referenceRatio != null && referenceRatio > 0) {
        return Math.abs(imageRatio - referenceRatio) > RATIO_TOLERANCE;
    }

    return false;
}

export function getOutputSize(aspectRatio) {
    const width = OUTPUT_WIDTH;
    // 横長は height を切り上げ（ratio が上限を超えない）、縦長は切り捨て（下限を下回らない）
    const height = Math.max(
        1,
        aspectRatio >= 1
            ? Math.ceil(width / aspectRatio)
            : Math.floor(width / aspectRatio),
    );

    return { width, height };
}

/** 書き出し後の実比率が Instagram 制限内に収まるか */
export function isOutputRatioValid(postType, width, height) {
    const ratio = width / height;
    return isRatioValidForPostType(postType, ratio);
}

/**
 * @param {HTMLCanvasElement} canvas  プレビュー用（表示サイズ）
 * @param {HTMLImageElement} img
 */
export function drawCropPreview(canvas, img, aspectRatio, scale, offsetX, offsetY) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const cw = canvas.width;
    const ch = canvas.height;
    const baseScale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
    const s = baseScale * scale;
    const drawW = img.naturalWidth * s;
    const drawH = img.naturalHeight * s;
    const x = (cw - drawW) / 2 + offsetX;
    const y = (ch - drawH) / 2 + offsetY;

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, x, y, drawW, drawH);
}

/**
 * プレビューキャンバスと同じアスペクト比で書き出す（offset はプレビュー座標系）
 */
export function exportCroppedBlobFromPreview(img, previewCanvas, aspectRatio, scale, offsetX, offsetY, quality = 0.92) {
    const { width, height } = getOutputSize(aspectRatio);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return Promise.reject(new Error('Canvas is not supported'));
    }

    const cw = previewCanvas.width;
    const ch = previewCanvas.height;
    const baseScale = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
    const s = baseScale * scale;
    const drawW = img.naturalWidth * s;
    const drawH = img.naturalHeight * s;
    const x = (cw - drawW) / 2 + offsetX;
    const y = (ch - drawH) / 2 + offsetY;

    const scaleX = width / cw;
    const scaleY = height / ch;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(
        img,
        x * scaleX,
        y * scaleY,
        drawW * scaleX,
        drawH * scaleY,
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('画像の書き出しに失敗しました。'))),
            'image/jpeg',
            quality,
        );
    });
}

export function blobToFile(blob, originalName) {
    const base = (originalName || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${base}-cropped.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

const ScheduledPostCrop = {
    FEED_CROP_PRESETS,
    STORY_CROP_PRESET,
    isVideoFile,
    readImageDimensions,
    isRatioValidForPostType,
    getCropPresets,
    pickDefaultCropRatio,
    needsCrop,
    getOutputSize,
    isOutputRatioValid,
    drawCropPreview,
    exportCroppedBlobFromPreview,
    blobToFile,
};

export default ScheduledPostCrop;

if (typeof window !== 'undefined') {
    window.ScheduledPostCrop = ScheduledPostCrop;
}
